import React, { useState, useEffect } from 'react';
import { listCases, deleteCase } from '../../services/factFinderApi';
import { exportCasePacketToPdf } from '../../utils/pdfExport';

const TYPE_LABELS = {
  discipline_investigation: 'Discipline / Investigation',
  safety_concern: 'Safety Concern',
  supervisor_directive_conflict: 'Supervisor Directive',
  contract_issue: 'Contract Issue',
  injury_near_miss: 'Injury / Near Miss',
  harassment_conduct: 'Harassment / Conduct',
  other: 'Other',
};

function RecentCases({ onOpenCase }) {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadCases();
  }, []);

  async function loadCases() {
    setLoading(true);
    setError('');
    try {
      const result = await listCases(5, 0);
      if (result.success) {
        setCases(result.cases || []);
      }
    } catch (err) {
      setError('Unable to load recent cases.');
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(caseId) {
    if (!window.confirm('Delete this case? This cannot be undone.')) return;
    try {
      const result = await deleteCase(caseId);
      if (result.success) {
        setCases(prev => prev.filter(c => (c._id || c.case_id) !== caseId));
      }
    } catch (err) {
      setError('Unable to delete case.');
    }
  }

  function handleExport(caseItem) {
    const analysis = caseItem.analysis_payload;
    const caseData = caseItem.case_payload;
    if (analysis && caseData) {
      exportCasePacketToPdf({ analysis, caseData });
    }
  }

  function formatDate(dateStr) {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  }

  if (loading) {
    return (
      <div className="ff-recent-cases" data-testid="recent-cases">
        <h2>Recent Cases</h2>
        <p className="ff-loading-text">Loading cases...</p>
      </div>
    );
  }

  if (cases.length === 0 && !error) {
    return null;
  }

  return (
    <div className="ff-recent-cases" data-testid="recent-cases">
      <h2>Recent Cases</h2>
      {error && <p className="ff-inline-error">{error}</p>}

      <div className="ff-cases-list">
        {cases.map((c) => {
          const caseId = c._id || c.case_id;
          return (
            <div key={caseId} className="ff-case-row" data-testid={`case-${caseId}`}>
              <div className="ff-case-info">
                <div className="ff-case-title">{c.title || 'Untitled Case'}</div>
                <div className="ff-case-meta">
                  <span className="ff-case-type">
                    {TYPE_LABELS[c.incident_type] || c.incident_type}
                  </span>
                  <span className="ff-case-date">{formatDate(c.created_at)}</span>
                  <span className={`ff-case-status ff-status-${c.status || 'draft'}`}>
                    {(c.status || 'draft').replace('_', ' ')}
                  </span>
                </div>
              </div>
              <div className="ff-case-actions">
                <button
                  className="ff-btn-outline ff-btn-sm"
                  onClick={() => onOpenCase(c)}
                  data-testid={`open-case-${caseId}`}
                >
                  Open
                </button>
                {c.analysis_payload && (
                  <button
                    className="ff-btn-outline ff-btn-sm"
                    onClick={() => handleExport(c)}
                    data-testid={`export-case-${caseId}`}
                  >
                    PDF
                  </button>
                )}
                <button
                  className="ff-btn-outline ff-btn-sm ff-btn-danger"
                  onClick={() => handleDelete(caseId)}
                  data-testid={`delete-case-${caseId}`}
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default RecentCases;
